import React, { useState, useEffect, useRef } from 'react';
import './TextToSpeech.css';

const TextToSpeech = () => {
  const [text, setText] = useState('');
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [isPrepared, setIsPrepared] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);

  const utteranceRef = useRef(null);
  const intervalRef = useRef(null);

  useEffect(() => {
    const synth = window.speechSynthesis;

    const loadVoices = () => {
      const availableVoices = synth.getVoices();
      setVoices(availableVoices);
      if (availableVoices.length > 0) {
        setSelectedVoice(availableVoices[0].name);
      }
    };

    if (synth.onvoiceschanged !== undefined) {
      synth.onvoiceschanged = loadVoices;
    }

    loadVoices();
  }, []);

  const handleSubmit = () => {
    if (!text.trim()) {
      alert('Please enter some text.');
      return;
    }

    if (utteranceRef.current) {
      window.speechSynthesis.cancel();
    }

    const utterance = new SpeechSynthesisUtterance(text);
    const voice = voices.find(v => v.name === selectedVoice);
    if (voice) utterance.voice = voice;

    utterance.volume = volume;
    utterance.rate = rate;
    utterance.pitch = pitch;

    utterance.onstart = () => {
      setIsSpeaking(true);
      setIsPaused(false);
      startProgress();
    };

    utterance.onend = () => {
      setIsSpeaking(false);
      setIsPaused(false);
      setProgress(100);
      setTimeout(() => {
        resetAll();
      }, 1000);
    };

    utterance.onpause = () => setIsPaused(true);
    utterance.onresume = () => setIsPaused(false);

    utteranceRef.current = utterance;
    setIsPrepared(true);
  };

  const startProgress = () => {
    setProgress(0);
    clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setProgress(prev => Math.min(prev + 2, 98));
    }, 200);
  };

  const resetAll = () => {
    setIsPrepared(false);
    setProgress(0);
    utteranceRef.current = null;
  };

  const handlePlay = () => {
    if (!utteranceRef.current) return;
    if (isPaused) {
      window.speechSynthesis.resume();
    } else {
      window.speechSynthesis.speak(utteranceRef.current);
    }
    setIsSpeaking(true);
    setIsPaused(false);
  };

  const handlePause = () => {
    window.speechSynthesis.pause();
    setIsPaused(true);
  };

  const handleStop = () => {
    window.speechSynthesis.cancel();
    resetAll();
  };

  return (
    <div className="tts-container">
      <h2>Text to Speech</h2>

      <textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="tts-textarea"
        placeholder="Enter text here..."
      />

      <div>
        <label htmlFor="voice">Select Voice: </label>
        <select
          id="voice"
          value={selectedVoice}
          onChange={(e) => setSelectedVoice(e.target.value)}
          className="tts-voice-select"
        >
          {voices.map((voice, index) => (
            <option key={index} value={voice.name}>
              {voice.name} ({voice.lang})
            </option>
          ))}
        </select>
      </div>

      <div className="tts-sliders">
        <label>Volume: {volume.toFixed(1)}</label>
        <input
          type="range"
          min="0"
          max="1"
          step="0.1"
          value={volume}
          onChange={e => setVolume(parseFloat(e.target.value))}
        />
        <label>Rate: {rate.toFixed(1)}</label>
        <input
          type="range"
          min="0.5"
          max="2"
          step="0.1"
          value={rate}
          onChange={e => setRate(parseFloat(e.target.value))}
        />
        <label>Pitch: {pitch.toFixed(1)}</label>
        <input
          type="range"
          min="0"
          max="2"
          step="0.1"
          value={pitch}
          onChange={e => setPitch(parseFloat(e.target.value))}
        />
      </div>

      <button onClick={handleSubmit} className="tts-submit-btn">
        Submit
      </button>

      {isPrepared && (
        <>
          <div className="tts-progress-bar">
            <div
              className={`tts-progress-fill ${isSpeaking ? 'animating' : ''}`}
              style={{ width: `${progress}%` }}
            />
          </div>

          <div className="tts-controls">
            <button onClick={handlePlay} disabled={isSpeaking && !isPaused}>
              {isPaused ? 'Resume ▶️' : isSpeaking ? 'Playing...' : 'Play ▶️'}
            </button>
            <button onClick={handlePause} disabled={!isSpeaking || isPaused}>
              Pause ⏸️
            </button>
            <button onClick={handleStop}>
              Stop 🛑
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default TextToSpeech;